<template>
  <div class="container-fluid p-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex align-items-center mb-3">
          <button class="btn btn-outline-secondary me-3" @click="goBack">
            <i class="bi bi-arrow-left me-2"></i>Back
          </button>
          <h2 class="mb-0 fw-bold gradient-text">Student Profile</h2>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading student profile...</p>
    </div>

    <!-- Student Profile Content -->
    <div v-else-if="student" class="row">
      <!-- Student Info Card -->
      <div class="col-lg-4 mb-4">
        <div class="modern-card p-4 text-center">
          <div class="student-avatar-large mb-3">
            {{ student.avatar || student.name?.charAt(0) || 'S' }}
          </div>
          <h4 class="fw-bold mb-1">{{ student.name }}</h4>
          <p class="text-muted mb-2">{{ student.email }}</p>
          <span class="badge bg-primary mb-3">{{ student.gradeLevel || 'Grade 3' }}</span>
          
          <!-- Quick Stats -->
          <div class="row text-center mt-4">
            <div class="col-4">
              <div class="stat-number">{{ student.stats.tasksCompleted }}</div>
              <div class="stat-label">Tasks Done</div>
            </div>
            <div class="col-4">
              <div class="stat-number">{{ student.stats.badgesEarned }}</div>
              <div class="stat-label">Badges</div>
            </div>
            <div class="col-4">
              <div class="stat-number">{{ student.stats.averageScore }}%</div>
              <div class="stat-label">Avg Score</div>
            </div>
          </div>
        </div>

        <!-- Contact Info -->
        <div class="modern-card p-4 mt-3">
          <h6 class="fw-bold mb-3">Contact Information</h6>
          <div class="mb-2">
            <small class="text-muted">Class Code</small>
            <div>{{ student.classCode || 'ABC123' }}</div>
          </div>
          <div class="mb-2">
            <small class="text-muted">Parent/Guardian</small>
            <div>{{ student.parentName || 'Not provided' }}</div>
          </div>
          <div class="mb-2">
            <small class="text-muted">Last Active</small>
            <div>{{ formatDate(student.lastActive) }}</div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="col-lg-8">
        
        <!-- Progress Overview -->
        <div class="modern-card p-4 mb-4">
          <h5 class="fw-bold mb-4">Progress Overview</h5>
          
          <!-- Progress Bars -->
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Reading Progress</label>
              <div class="progress mb-2">
                <div 
                  class="progress-bar bg-success" 
                  :style="{ width: student.progress.reading + '%' }"
                ></div>
              </div>
              <small class="text-muted">{{ student.progress.reading }}% Complete</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Math Progress</label>
              <div class="progress mb-2">
                <div 
                  class="progress-bar bg-info" 
                  :style="{ width: student.progress.math + '%' }"
                ></div>
              </div>
              <small class="text-muted">{{ student.progress.math }}% Complete</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Language Arts</label>
              <div class="progress mb-2">
                <div 
                  class="progress-bar bg-warning" 
                  :style="{ width: student.progress.language + '%' }"
                ></div>
              </div>
              <small class="text-muted">{{ student.progress.language }}% Complete</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Life Skills</label>
              <div class="progress mb-2">
                <div 
                  class="progress-bar bg-primary" 
                  :style="{ width: student.progress.lifeSkills + '%' }"
                ></div>
              </div>
              <small class="text-muted">{{ student.progress.lifeSkills }}% Complete</small>
            </div>
          </div>
        </div>

        <!-- Recent Activities -->
        <div class="modern-card p-4 mb-4">
          <h5 class="fw-bold mb-4">Recent Activities</h5>
          <div class="activity-timeline">
            <div 
              v-for="activity in student.recentActivities" 
              :key="activity.id"
              class="activity-item d-flex align-items-center mb-3"
            >
              <div class="activity-icon me-3">
                <i :class="getActivityIcon(activity.type)"></i>
              </div>
              <div class="flex-grow-1">
                <div class="fw-bold">{{ activity.title }}</div>
                <small class="text-muted">{{ activity.description }}</small>
              </div>
              <div class="text-end">
                <div class="fw-bold text-success" v-if="activity.score">{{ activity.score }}%</div>
                <small class="text-muted">{{ formatDate(activity.date) }}</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Assignments & Tasks -->
        <div class="modern-card p-4">
          <h5 class="fw-bold mb-4">Current Assignments</h5>
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Assignment</th>
                  <th>Subject</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="assignment in student.assignments" :key="assignment.id">
                  <td>{{ assignment.title }}</td>
                  <td>{{ assignment.subject }}</td>
                  <td>{{ formatDate(assignment.dueDate) }}</td>
                  <td>
                    <span 
                      class="badge" 
                      :class="getStatusBadgeClass(assignment.status)"
                    >
                      {{ assignment.status }}
                    </span>
                  </td>
                  <td>
                    <span v-if="assignment.score" class="fw-bold">{{ assignment.score }}%</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>

    <!-- Error State -->
    <div v-else class="text-center py-5">
      <i class="bi bi-exclamation-triangle text-warning" style="font-size: 3rem;"></i>
      <h5 class="mt-3">Student Not Found</h5>
      <p class="text-muted">The requested student profile could not be loaded.</p>
      <button class="btn btn-primary" @click="goBack">Go Back</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StudentProfile',
  data() {
    return {
      loading: true,
      student: null,
      studentId: null
    }
  },
  mounted() {
    this.studentId = this.$route.params.id || this.$route.query.studentId
    this.loadStudentProfile()
  },
  methods: {
    async loadStudentProfile() {
      try {
        this.loading = true
        const userId = this.studentId
        // Fetch progress overview
        const [progressRes] = await Promise.all([
          fetch(`${process.env.VUE_APP_API_URL || 'http://127.0.0.1:5000'}/api/progress/${userId}`),
          // Optionally fetch leaderboard here if you want to show it on profile
          // fetch(`${process.env.VUE_APP_API_URL || 'http://127.0.0.1:5000'}/api/leaderboard/${userId}`)
        ])
        const progressData = progressRes.ok ? await progressRes.json() : null
        // const leaderboardData = leaderboardRes.ok ? await leaderboardRes.json() : []

        // Minimal student info; augment if you have a user profile endpoint
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const name = currentUser?.name || `Student ${userId}`
        const email = currentUser?.email || ''

        // Map backend progress to UI model
        const readingPct = 0 // not available in /api/progress; keep 0 or derive from your domain
        const mathPct = 0
        const languagePct = 0
        const lifePct = 0

        this.student = {
          id: userId,
          name,
          email,
          avatar: name?.charAt(0) || 'S',
          gradeLevel: progressData?.grade ? `Grade ${progressData.grade}` : '',
          classCode: '',
          parentName: '',
          lastActive: progressData?.last_active,
          stats: {
            tasksCompleted: progressData?.tasks_completed || 0,
            badgesEarned: progressData?.badges_earned || 0,
            averageScore: progressData?.avg_score || 0
          },
          progress: {
            reading: readingPct,
            math: mathPct,
            language: languagePct,
            lifeSkills: lifePct
          },
          recentActivities: [],
          assignments: []
        }

        this.loading = false
      } catch (e) {
        console.error('Failed to load student profile:', e)
        this.loading = false
        this.student = null
      }
    },

    getActivityIcon(type) {
      const icons = {
        task: 'bi bi-check-circle text-success',
        badge: 'bi bi-award text-warning',
        assignment: 'bi bi-file-text text-primary',
        reading: 'bi bi-book text-info'
      }
      return icons[type] || 'bi bi-circle text-secondary'
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Completed': 'bg-success',
        'Submitted': 'bg-info',
        'In Progress': 'bg-warning',
        'Not Started': 'bg-secondary',
        'Overdue': 'bg-danger'
      }
      return classes[status] || 'bg-secondary'
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },

    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.student-avatar-large {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: var(--role-gradient);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: bold;
  margin: 0 auto;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--role-primary);
}

.stat-label {
  font-size: 0.8rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.activity-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.progress {
  height: 8px;
  border-radius: 4px;
}

.table th {
  border-top: none;
  font-weight: 600;
  color: var(--text-primary);
}

.badge {
  font-size: 0.75rem;
}
</style> 